import { Injectable } from '@angular/core';
import type { Components } from '@chefami-monorepo/ui-components/types';

type HTMLChefNotificationElement = HTMLElement & Components.ChefNotification;

/**
 * Angular service wrapper for the chef-notification Stencil component.
 * Provides a convenient API to show toast notifications.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private notification: HTMLChefNotificationElement | null = null;

  private getNotification(): HTMLChefNotificationElement | null {
    if (!this.notification) {
      this.notification = document.querySelector(
        'chef-notification'
      ) as HTMLChefNotificationElement | null;
    }
    return this.notification;
  }

  /**
   * Show a success notification
   * @param message - Message to display
   * @param autoCloseMs - Optional auto-close timeout (default: 5000ms)
   * @returns Promise resolving to notification ID
   */
  success(message: string, autoCloseMs?: number): Promise<string> {
    return (
      this.getNotification()?.success(message, autoCloseMs) ??
      Promise.resolve('')
    );
  }

  /**
   * Show an info notification
   * @param message - Message to display
   * @param autoCloseMs - Optional auto-close timeout (default: 5000ms)
   * @returns Promise resolving to notification ID
   */
  info(message: string, autoCloseMs?: number): Promise<string> {
    return (
      this.getNotification()?.info(message, autoCloseMs) ?? Promise.resolve('')
    );
  }

  /**
   * Show an error notification
   * @param message - Message to display
   * @param autoCloseMs - Optional auto-close timeout (default: 10000ms)
   * @returns Promise resolving to notification ID
   */
  error(message: string, autoCloseMs?: number): Promise<string> {
    return (
      this.getNotification()?.error(message, autoCloseMs) ?? Promise.resolve('')
    );
  }

  /**
   * Show a notification with custom type
   * @param type - Notification type (success, info, error)
   * @param message - Message to display
   * @param autoCloseMs - Optional auto-close timeout
   * @returns Promise resolving to notification ID
   */
  show(
    type: 'success' | 'info' | 'error',
    message: string,
    autoCloseMs?: number
  ): Promise<string> {
    return (
      this.getNotification()?.show(type, message, autoCloseMs) ??
      Promise.resolve('')
    );
  }

  /**
   * Remove a specific notification by ID
   * @param id - Notification ID to remove
   */
  dismiss(id: string): Promise<void> {
    return this.getNotification()?.dismiss(id) ?? Promise.resolve();
  }

  /**
   * Clear all notifications
   */
  clear(): Promise<void> {
    return this.getNotification()?.clear() ?? Promise.resolve();
  }
}
